import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import type { HeroCatalogItem } from "@scrile/contracts";
import { loadHeroCatalog } from "../api/catalog";
import { Icon } from "../components/Icon";
import { t } from "../i18n";
import { heroAddress } from "../lib/site";
import { platformRoute } from "../lib/platformRoute";
import "./platform.css";

type CatalogFilter = "all" | "audio" | "chat";

const CHARACTER_DETAILS: Record<string, { mood: string; line: string }> = {
  chef: { mood: "Slow burn", line: "“You came. Good. I kept the stove warm.”" },
  luca: { mood: "Slow burn", line: "“You came. Good. I kept the stove warm.”" },
  mira: { mood: "Wonder", line: "“The pulse came back. Seventeen minutes, exactly.”" },
  ava: { mood: "Butterflies", line: "“Wrong number. But… are you awake anyway?”" },
  theo: { mood: "Secrets", line: "“Still awake? I found something in the projection room.”" },
};

/** Общая витрина: ночная mood-first подача, но каталог и адреса всегда приходят с API. */
export function PlatformPage({ baseDomain, studioUrl }: { baseDomain: string; studioUrl: string }) {
  const [slug, setSlug] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [catalog, setCatalog] = useState<HeroCatalogItem[] | null>(null);
  const [catalogUnavailable, setCatalogUnavailable] = useState(false);
  const [filter, setFilter] = useState<CatalogFilter>("all");
  const route = platformRoute(window.location.pathname);
  const home = route === "home";

  useEffect(() => {
    document.title = route === "home"
      ? "Scrile AI — Someone is waiting up"
      : route === "creators"
        ? "For creators — Scrile AI"
        : route === "partners"
          ? "Partner program — Scrile AI"
        : "Page not found — Scrile AI";
    document.documentElement.classList.add("platform-site");
    document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute("content", "Scrile AI");
    return () => document.documentElement.classList.remove("platform-site");
  }, [route]);

  useEffect(() => {
    if (!home) return;
    const controller = new AbortController();
    setCatalogUnavailable(false);
    void loadHeroCatalog({ signal: controller.signal })
      .then((heroes) => setCatalog(heroes))
      .catch(() => {
        if (!controller.signal.aborted) setCatalogUnavailable(true);
      });
    return () => controller.abort();
  }, [home]);

  const visibleCatalog = useMemo(
    () => catalog?.filter((hero) => filter === "all" || hero.format === filter) ?? null,
    [catalog, filter],
  );

  function visit(event: FormEvent) {
    event.preventDefault();
    const address = heroAddress(slug, baseDomain);
    if (!address) {
      setInvalid(true);
      return;
    }
    window.location.assign(address);
  }

  return (
    <div className={`platform${route === "creators" ? " platform--creators" : route === "partners" ? " platform--partners" : ""}`}>
      <a className="platform-skip" href="#main">{t("platform.skip")}</a>
      <header className="platform-header platform-wrap">
        <a className="platform-brand" href="/" aria-label={t("platform.home")}>
          <span>Scrile</span> <em>AI</em>
        </a>
        <nav className="platform-nav-links" aria-label={t("platform.nav")}>
          {home && <a href="#characters">{t("platform.catalog.nav")}</a>}
          <a href="/creators" aria-current={route === "creators" ? "page" : undefined}>{t("platform.creators.nav")}</a>
          <a href="/partners" aria-current={route === "partners" ? "page" : undefined}>{t("platform.partners.nav")}</a>
          <a className="platform-studio" href={studioUrl}>{t("platform.studio")}<Icon name="chevronRight" size={15} /></a>
        </nav>
      </header>

      {home ? <main id="main" tabIndex={-1}>
        <section className="platform-intro platform-wrap">
          <div className="platform-intro__copy">
            <p className="platform-kicker">{t("platform.eyebrow")}</p>
            <h1>{t("platform.title.before")} <em>{t("platform.title.emphasis")}</em> {t("platform.title.after")}</h1>
            <p className="platform-lede">{t("platform.intro")}</p>
            <div className="platform-intro__actions">
              <a className="platform-button" href="#characters">{t("platform.explore")}<Icon name="arrowDown" size={18} /></a>
              <span>{t("platform.freeNote")}</span>
            </div>
          </div>
          <div className="platform-night-card" aria-hidden="true">
            <div className="platform-night-card__glow" />
            <div className="platform-night-card__top"><span>01:12</span><span>{t("platform.awake")}</span></div>
            <div className="platform-night-card__portrait"><img src="/characters/luca.jpg" alt="" /></div>
            <p>“You came. Good.”</p>
            <div className="platform-night-card__wave">{Array.from({ length: 34 }, (_, index) => <i key={index} style={{ height: `${18 + ((index * 23) % 58)}%` }} />)}</div>
            <div className="platform-night-card__caption">Luca · First Dinner · Episode 1</div>
          </div>
        </section>

        <section id="characters" className="platform-catalog platform-wrap" aria-labelledby="characters-title" aria-busy={catalog === null && !catalogUnavailable}>
          <div className="platform-section-heading">
            <div><p className="platform-kicker">{t("platform.catalog.label")}</p><h2 id="characters-title">{t("platform.catalog.title")}</h2></div>
            <p>{t("platform.catalog.body")}</p>
          </div>
          <div className="platform-filters" role="group" aria-label={t("platform.catalog.filterLabel")}>
            {(["all", "audio", "chat"] as const).map((value) => (
              <button type="button" key={value} className={filter === value ? "is-active" : ""} onClick={() => setFilter(value)} aria-pressed={filter === value}>
                {t(`platform.catalog.filter.${value}`)}
              </button>
            ))}
            {visibleCatalog && <span>{t("platform.catalog.awakeCount", { count: visibleCatalog.length })}</span>}
          </div>
          {visibleCatalog && visibleCatalog.length > 0 ? <div className="platform-character-grid">
            {visibleCatalog.map((hero) => {
              const detail = CHARACTER_DETAILS[hero.slug] ?? { mood: hero.format === "audio" ? "Voice" : "Chat", line: `“${hero.tagline}”` };
              return <a className="platform-character" href={hero.url} key={hero.slug} style={{ "--character-accent": hero.accent, "--character-background": hero.background } as CSSProperties}>
                <div className="platform-character-media">
                  {hero.coverUrl && <img className="platform-character-cover" src={hero.coverUrl} alt="" loading="lazy" onError={(event) => { event.currentTarget.hidden = true; }} />}
                  <span className="platform-character-initial" aria-hidden="true">{hero.name.slice(0, 1)}</span>
                  {hero.portraitUrl && <img className="platform-character-portrait" src={hero.portraitUrl} alt="" loading="lazy" onError={(event) => { event.currentTarget.hidden = true; }} />}
                  <span className={`platform-character-format platform-character-format--${hero.format}`}><Icon name={hero.format === "chat" ? "sparkle" : "voice"} size={15} />{t(hero.format === "chat" ? "platform.catalog.chat" : "platform.catalog.audio")}</span>
                  <span className="platform-character-mood">{detail.mood}</span>
                </div>
                <div className="platform-character-copy">
                  <div><h3>{hero.name}</h3><span>{hero.format === "chat" ? "Text" : "Voice"}</span></div>
                  <blockquote>{detail.line}</blockquote>
                  <p>{hero.tagline}</p>
                  <strong>{t(hero.format === "chat" ? "platform.catalog.openChat" : "platform.catalog.listen")}<Icon name="chevronRight" size={17} /></strong>
                </div>
              </a>;
            })}
          </div> : catalog === null && !catalogUnavailable ? <div className="platform-character-grid" aria-label={t("platform.catalog.loading")}>
            {Array.from({ length: 4 }, (_, index) => <div className="platform-character platform-character--loading" aria-hidden="true" key={index}><div className="platform-character-media" /><div className="platform-character-copy"><i /><i /></div></div>)}
          </div> : <div className="platform-catalog-empty"><p>{t(catalogUnavailable ? "platform.catalog.unavailable" : "platform.catalog.empty")}</p><a href="#find-hero">{t("platform.catalog.find")}<Icon name="arrowDown" size={16} /></a></div>}
        </section>

        <section className="platform-promises platform-wrap" aria-label={t("platform.promises.label")}>
          <article><span>01</span><h3>{t("platform.promises.memory.title")}</h3><p>{t("platform.promises.memory.body")}</p></article>
          <article><span>02</span><h3>{t("platform.promises.schedule.title")}</h3><p>{t("platform.promises.schedule.body")}</p></article>
          <article><span>03</span><h3>{t("platform.promises.formats.title")}</h3><p>{t("platform.promises.formats.body")}</p></article>
        </section>

        <section id="find-hero" className="platform-find platform-wrap" aria-labelledby="find-title">
          <div><p className="platform-kicker">{t("platform.find.label")}</p><h2 id="find-title">{t("platform.find.title")}</h2><p>{t("platform.find.body")}</p></div>
          <form onSubmit={visit} noValidate>
            <label className="sr-only" htmlFor="hero-slug">{t("platform.find.label")}</label>
            <div className="platform-address"><input id="hero-slug" value={slug} onChange={(event) => { setSlug(event.target.value); setInvalid(false); }} placeholder="luca" maxLength={63} autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="off" aria-invalid={invalid} aria-describedby={invalid ? "hero-address-error" : "hero-address-hint"} /><span>.{baseDomain}</span><button type="submit" aria-label={t("platform.find.open")}><Icon name="chevronRight" size={18} /></button></div>
            <p id={invalid ? "hero-address-error" : "hero-address-hint"} className={invalid ? "platform-error" : "platform-hint"} role={invalid ? "alert" : undefined}>{t(invalid ? "platform.find.invalid" : "platform.find.hint")}</p>
          </form>
        </section>

        <section className="platform-creator platform-wrap" aria-labelledby="creator-title">
          <p className="platform-kicker">{t("platform.creator.label")}</p><h2 id="creator-title">{t("platform.creator.title")}</h2><p>{t("platform.creator.body")}</p>
          <a className="platform-button platform-button--light" href="/creators">{t("platform.creator.cta")}<Icon name="chevronRight" size={18} /></a>
        </section>
      </main> : route === "creators" ? <CreatorLanding studioUrl={studioUrl} /> : route === "partners" ? <PartnerLanding /> : <main id="main" className="platform-missing platform-wrap" tabIndex={-1}><h1>{t("app.notFound.title")}</h1><p>{t("platform.missing")}</p><a className="platform-button" href="/">{t("platform.home")}</a></main>}

      <footer className="platform-footer platform-wrap"><span>Scrile <em>AI</em></span><p>{t("platform.footer")}</p></footer>
    </div>
  );
}

function PartnerLanding() {
  const applyUrl = "mailto:admin@ai.scrile.com?subject=Scrile%20AI%20partner%20application";
  return <main id="main" className="platform-partners" tabIndex={-1}>
    <section className="platform-partners-intro platform-wrap">
      <div>
        <p className="platform-kicker">{t("platform.partners.eyebrow")}</p>
        <h1>{t("platform.partners.title.before")} <em>{t("platform.partners.title.emphasis")}</em></h1>
        <p className="platform-lede">{t("platform.partners.intro")}</p>
        <a className="platform-button" href={applyUrl}>{t("platform.partners.cta")}<Icon name="chevronRight" size={18} /></a>
      </div>
      <div className="partner-link-card" aria-label={t("platform.partners.card.label")}>
        <small>{t("platform.partners.card.label")}</small>
        <strong>luca.ai.scrile.com/?ref=NIGHT-VOICE</strong>
        <div><span>1</span><p><b>{t("platform.partners.card.visit")}</b>{t("platform.partners.card.visitBody")}</p></div>
        <div><span>2</span><p><b>{t("platform.partners.card.join")}</b>{t("platform.partners.card.joinBody")}</p></div>
        <div><span>3</span><p><b>{t("platform.partners.card.earn")}</b>{t("platform.partners.card.earnBody")}</p></div>
      </div>
    </section>

    <section className="platform-partners-details platform-wrap" aria-labelledby="partner-details-title">
      <p className="platform-kicker">{t("platform.partners.details.label")}</p>
      <h2 id="partner-details-title">{t("platform.partners.details.title")}</h2>
      <div>
        <article><span>01</span><h3>{t("platform.partners.links.title")}</h3><p>{t("platform.partners.links.body")}</p></article>
        <article><span>02</span><h3>{t("platform.partners.attribution.title")}</h3><p>{t("platform.partners.attribution.body")}</p></article>
        <article><span>03</span><h3>{t("platform.partners.payouts.title")}</h3><p>{t("platform.partners.payouts.body")}</p></article>
      </div>
    </section>

    <section className="platform-partners-final platform-wrap">
      <div><p className="platform-kicker">{t("platform.partners.final.label")}</p><h2>{t("platform.partners.final.title")}</h2><p>{t("platform.partners.final.body")}</p></div>
      <a className="platform-button platform-button--light" href={applyUrl}>{t("platform.partners.cta")}<Icon name="chevronRight" size={18} /></a>
    </section>
  </main>;
}

function CreatorLanding({ studioUrl }: { studioUrl: string }) {
  return <main id="main" className="platform-creators" tabIndex={-1}>
    <section className="platform-creators-intro platform-wrap">
      <div>
        <p className="platform-kicker">{t("platform.creators.eyebrow")}</p>
        <h1>{t("platform.creators.title.before")} <em>{t("platform.creators.title.emphasis")}</em></h1>
        <p className="platform-lede">{t("platform.creators.intro")}</p>
        <div className="platform-creators-actions"><a className="platform-button" href={studioUrl}>{t("platform.creators.cta")}<Icon name="chevronRight" size={18} /></a><a href="#creator-workflow">{t("platform.creators.explore")}</a></div>
      </div>
      <div className="story-bible" aria-label={t("platform.creators.bible.label")}>
        <div className="story-bible__head"><strong>Story Bible · First Dinner</strong><span>v3 · live</span></div>
        <article><small>{t("platform.creators.bible.season")}</small><p>A patient man, one seat, eight lessons in attention — and the person who keeps ordering anonymously.</p></article>
        <article><small>{t("platform.creators.bible.episode")}</small><p>The anonymous order arrives with a note in handwriting Luca recognises.</p></article>
        <article><small>{t("platform.creators.bible.scene")}</small><p>Kitchen, 23:40. Future reveals stay hidden from the model.</p></article>
      </div>
    </section>

    <section id="creator-workflow" className="platform-creators-steps platform-wrap" aria-labelledby="creator-steps-title">
      <p className="platform-kicker">{t("platform.creators.steps.label")}</p><h2 id="creator-steps-title">{t("platform.creators.steps.title")}</h2>
      <ol>
        <li><span>01</span><div><h3>{t("platform.creators.step1.title")}</h3><p>{t("platform.creators.step1.body")}</p></div></li>
        <li><span>02</span><div><h3>{t("platform.creators.step2.title")}</h3><p>{t("platform.creators.step2.body")}</p></div></li>
        <li><span>03</span><div><h3>{t("platform.creators.step3.title")}</h3><p>{t("platform.creators.step3.body")}</p></div></li>
        <li><span>04</span><div><h3>{t("platform.creators.step4.title")}</h3><p>{t("platform.creators.step4.body")}</p></div></li>
      </ol>
    </section>

    <section className="platform-creators-formats platform-wrap" aria-labelledby="creator-formats-title">
      <div className="platform-section-heading"><div><p className="platform-kicker">{t("platform.creators.formats.label")}</p><h2 id="creator-formats-title">{t("platform.creators.formats.title")}</h2></div><p>{t("platform.creators.formats.body")}</p></div>
      <div className="platform-creator-format-grid">
        <article><span><Icon name="voice" size={24} /></span><p className="platform-kicker">Voice</p><h3>{t("platform.creators.audio.title")}</h3><p>{t("platform.creators.audio.body")}</p><a href="https://luca.ai.scrile.com">{t("platform.creators.audio.cta")}<Icon name="chevronRight" size={16} /></a></article>
        <article><span><Icon name="sparkle" size={24} /></span><p className="platform-kicker">Text</p><h3>{t("platform.creators.chat.title")}</h3><p>{t("platform.creators.chat.body")}</p><a href="https://theo.ai.scrile.com">{t("platform.creators.chat.cta")}<Icon name="chevronRight" size={16} /></a></article>
      </div>
    </section>

    <section className="platform-creators-business platform-wrap" aria-labelledby="creator-business-title">
      <div><p className="platform-kicker">{t("platform.creators.business.label")}</p><h2 id="creator-business-title">{t("platform.creators.business.title")}</h2><p>{t("platform.creators.business.body")}</p></div>
      <ul>
        <li><Icon name="check" size={18} /><span><strong>{t("platform.creators.business.pricing.title")}</strong>{t("platform.creators.business.pricing.body")}</span></li>
        <li><Icon name="check" size={18} /><span><strong>{t("platform.creators.business.audience.title")}</strong>{t("platform.creators.business.audience.body")}</span></li>
        <li><Icon name="check" size={18} /><span><strong>{t("platform.creators.business.tools.title")}</strong>{t("platform.creators.business.tools.body")}</span></li>
      </ul>
    </section>

    <section className="platform-creators-final platform-wrap">
      <div><p className="platform-kicker">{t("platform.creators.final.label")}</p><h2>{t("platform.creators.final.title")}</h2><p>{t("platform.creators.final.body")}</p></div>
      <a className="platform-button platform-button--light" href={studioUrl}>{t("platform.creators.cta")}<Icon name="chevronRight" size={18} /></a>
    </section>
  </main>;
}
